import Products from 'merchandise-lib/constants/Product';
import { useGrowthPopPurchaseOptions } from '../v2/hooks/pricing/useGrowthPurchaseOptions';
import { useProductDetailsForArray } from '../v2/hooks/product/useProductDetails';
import { useIsDeveloperPortal, useIsInAppAuth, useIsSeatsUser } from '../v2/hooks/user/useUserInfo';
import { useGlobalLoading } from './loadingHooks';
import { useIsHubSpotOnCreditsPilot } from './useIsHubSpotOnCreditsPilot';
const CORE_SEATS_API_NAMES = [Products.CORE_SEATS_STARTER, Products.CORE_SEATS_PROFESSIONAL, Products.CORE_SEATS_ENTERPRISE];
const FLEX_SEATS_API_NAMES = [Products.FLEX_SEAT];
const ENTERPRISE_FLEX_BUNDLE_API_NAMES = [Products.ENTERPRISE_FLEX_PROGRAM];
const HSOC_CORE_SEATS_API_NAMES = [Products.CORE_SEATS_STARTER_HSOC, Products.CORE_SEATS_PROFESSIONAL_HSOC, Products.CORE_SEATS_ENTERPRISE_HSOC];
const CRM_SUITE_BUNDLE_API_NAMES = [Products.CRM_SUITE_STARTER_BUNDLE, Products.CRM_SUITE_PROFESSIONAL_BUNDLE, Products.CRM_SUITE_ENTERPRISE_BUNDLE];
export const useMismatchedCohortingCheck = () => {
  const isAppReady = useGlobalLoading();
  const hasSeatsAccessScope = useIsSeatsUser();
  const isDeveloperPortal = useIsDeveloperPortal();
  const isAppAuth = useIsInAppAuth();
  const {
    isInPilot: isHSoCPilot,
    loading: isHSoCPilotLoading
  } = useIsHubSpotOnCreditsPilot();
  const CORE_SEATS_API_NAMES_TO_USE = isHSoCPilot ? HSOC_CORE_SEATS_API_NAMES : CORE_SEATS_API_NAMES;
  const shouldSkip = !isAppAuth || isHSoCPilotLoading;
  const {
    products: purchaseableProducts,
    loading: isPopDataLoading,
    error: popDataError
  } = useGrowthPopPurchaseOptions({
    productsToPurchase: [...CORE_SEATS_API_NAMES_TO_USE.map(productApiName => ({
      productApiName,
      quantity: 1
    })), ...FLEX_SEATS_API_NAMES.map(productApiName => ({
      productApiName,
      quantity: 1
    })), ...CRM_SUITE_BUNDLE_API_NAMES.map(productApiName => ({
      productApiName,
      quantity: 1
    })), ...ENTERPRISE_FLEX_BUNDLE_API_NAMES.map(productApiName => ({
      productApiName,
      quantity: 1
    }))],
    skip: shouldSkip
  });
  const hasAnyCoreSeatsProductsInPopData = !isPopDataLoading && !popDataError && CORE_SEATS_API_NAMES_TO_USE.some(apiName => {
    return !!(purchaseableProducts !== null && purchaseableProducts !== void 0 && purchaseableProducts[apiName]);
  });
  const hasAnyFlexSeatsProductsInPopData = !isPopDataLoading && !popDataError && FLEX_SEATS_API_NAMES.some(apiName => {
    return !!(purchaseableProducts !== null && purchaseableProducts !== void 0 && purchaseableProducts[apiName]);
  });
  const hasAnySuiteBundleProductsInPopData = !isPopDataLoading && !popDataError && CRM_SUITE_BUNDLE_API_NAMES.some(apiName => {
    return !!(purchaseableProducts !== null && purchaseableProducts !== void 0 && purchaseableProducts[apiName]);
  });
  const hasAnyEnterpriseFlexBundleProductsInPopData = !isPopDataLoading && !popDataError && ENTERPRISE_FLEX_BUNDLE_API_NAMES.some(apiName => {
    return !!(purchaseableProducts !== null && purchaseableProducts !== void 0 && purchaseableProducts[apiName]);
  });
  const {
    productDetails,
    loading: isProductDetailsLoading,
    error: productDetailsError
  } = useProductDetailsForArray({
    productApiNames: [...CORE_SEATS_API_NAMES_TO_USE, ...CRM_SUITE_BUNDLE_API_NAMES, ...FLEX_SEATS_API_NAMES, ...ENTERPRISE_FLEX_BUNDLE_API_NAMES],
    skip: shouldSkip
  });
  const hasAnyCoreSeatsInProductData = !isProductDetailsLoading && !productDetailsError && CORE_SEATS_API_NAMES_TO_USE.some(apiName => {
    var _productDetails$produ;
    return !!(productDetails !== null && productDetails !== void 0 && (_productDetails$produ = productDetails.productsByName) !== null && _productDetails$produ !== void 0 && _productDetails$produ[apiName]);
  });
  const hasAnyFlexSeatsInProductData = !isProductDetailsLoading && !productDetailsError && FLEX_SEATS_API_NAMES.some(apiName => {
    var _productDetails$produ2;
    return !!(productDetails !== null && productDetails !== void 0 && (_productDetails$produ2 = productDetails.productsByName) !== null && _productDetails$produ2 !== void 0 && _productDetails$produ2[apiName]);
  });
  const hasAnySuiteBundlesInProductData = !isProductDetailsLoading && !productDetailsError && CRM_SUITE_BUNDLE_API_NAMES.some(apiName => {
    var _productDetails$produ3;
    return !!(productDetails !== null && productDetails !== void 0 && (_productDetails$produ3 = productDetails.productsByName) !== null && _productDetails$produ3 !== void 0 && _productDetails$produ3[apiName]);
  });
  const hasAnyEnterpriseFlexBundlesInProductData = !isProductDetailsLoading && !productDetailsError && ENTERPRISE_FLEX_BUNDLE_API_NAMES.some(apiName => {
    var _productDetails$produ4;
    return !!(productDetails !== null && productDetails !== void 0 && (_productDetails$produ4 = productDetails.productsByName) !== null && _productDetails$produ4 !== void 0 && _productDetails$produ4[apiName]);
  });
  const hasCoreSeats = hasAnyCoreSeatsInProductData && hasAnyCoreSeatsProductsInPopData;

  // EFP SKU includes 50 flex seats, customer doesn't HAVE to have additional flex seats
  const hasFlexSeats = hasAnyFlexSeatsInProductData && hasAnyFlexSeatsProductsInPopData || hasAnyEnterpriseFlexBundlesInProductData && hasAnyEnterpriseFlexBundleProductsInPopData;
  const preSeatsCohort = isAppAuth ? !hasSeatsAccessScope && !hasCoreSeats && !hasFlexSeats : false;
  const seatsCohort = isAppAuth ? (hasSeatsAccessScope || isDeveloperPortal) && (hasFlexSeats || hasCoreSeats) : true;
  const isLoading = !isAppReady || isPopDataLoading || isProductDetailsLoading || isHSoCPilotLoading;
  const hasMismatchedProductsAndPopData = hasAnySuiteBundlesInProductData !== hasAnySuiteBundleProductsInPopData || hasAnyCoreSeatsInProductData !== hasAnyCoreSeatsProductsInPopData || hasAnyFlexSeatsInProductData !== hasAnyFlexSeatsProductsInPopData || hasAnyEnterpriseFlexBundlesInProductData !== hasAnyEnterpriseFlexBundleProductsInPopData;
  const hasMismatchedSeatsGating = !isLoading && preSeatsCohort === seatsCohort;
  const mismatchedSeatsGatingMetrics = hasMismatchedSeatsGating ? {
    preSeatsCohort,
    seatsCohort,
    isAppAuth,
    hasSeatsAccessScope,
    isDeveloperPortal,
    hasCoreSeats,
    hasFlexSeats,
    hasAnyCoreSeatsInProductData,
    hasAnyCoreSeatsProductsInPopData,
    hasAnyFlexSeatsInProductData,
    hasAnyFlexSeatsProductsInPopData,
    hasAnyEnterpriseFlexBundlesInProductData,
    hasAnyEnterpriseFlexBundleProductsInPopData,
    isHSoCPilot
  } : undefined;
  const mismatchedProductsAndPopDataMetrics = hasMismatchedProductsAndPopData ? {
    hasAnyCoreSeatsInProductData,
    hasAnyCoreSeatsProductsInPopData,
    hasAnyFlexSeatsInProductData,
    hasAnyFlexSeatsProductsInPopData,
    hasAnyEnterpriseFlexBundlesInProductData,
    hasAnyEnterpriseFlexBundleProductsInPopData,
    hasAnySuiteBundlesInProductData,
    hasAnySuiteBundleProductsInPopData,
    isHSoCPilot,
    popDataError: popDataError ? String(popDataError) : undefined,
    productDetailsError: productDetailsError ? String(productDetailsError) : undefined
  } : undefined;
  if (!isAppAuth) {
    return {
      loading: false,
      hasMismatchedProductsAndPopData: false,
      hasMismatchedSeatsGating: false,
      mismatchedSeatsGatingMetrics: undefined,
      mismatchedProductsAndPopDataMetrics: undefined
    };
  }
  return {
    loading: isLoading,
    hasMismatchedProductsAndPopData,
    hasMismatchedSeatsGating,
    mismatchedSeatsGatingMetrics,
    mismatchedProductsAndPopDataMetrics
  };
};